import { Execution, VideoRecord } from "../types";

export const LATEST_EXECUTION_FILTER_ID = "latest";

export const normalizeExecutionStatus = (status: string | undefined | null): string => {
    const normalized = status?.toLowerCase() ?? "none";
    return normalized === "created" ? "started" : normalized;
};

const getSortedExecutions = (record: VideoRecord): Execution[] => {
    return [...record.executions].sort((a, b) => a.id - b.id);
};

export const getLatestExecution = (record: VideoRecord): Execution | undefined => {
    return getSortedExecutions(record).at(-1);
};

export const getExecutionsForWorkflowType = (record: VideoRecord, workflowType?: string): Execution[] => {
    if (!workflowType || workflowType === LATEST_EXECUTION_FILTER_ID) {
        const latest = getLatestExecution(record);
        return latest ? [latest] : [];
    }
    return record.executions.filter((execution) => execution.type === workflowType);
};

export const hasExecutionWithStatuses = (
    record: VideoRecord,
    statuses: string[],
    workflowType?: string,
): boolean => {
    const normalizedStatuses = statuses.map(normalizeExecutionStatus);
    const executions = getExecutionsForWorkflowType(record, workflowType);
    if (normalizedStatuses.includes("none")) {
        return executions.length === 0;
    }
    return executions.some((execution) => normalizedStatuses.includes(normalizeExecutionStatus(execution.status)));
};

export type RecordsExecutionsSummary = {
    executionName: string;
    nbRecords: number;
    nbRecordsCompleted: number;
    nbRecordsInError: number;
    nbRecordsRunning: number;
};

export const getExecutionStatus = (record: VideoRecord, executionName?: string): string => {
    const executions = !executionName || executionName === LATEST_EXECUTION_FILTER_ID
        ? record.executions
        : record.executions.filter(e => e.type === executionName);

    if (executions.length === 0) return "none";

    const sorted = [...executions].sort((a, b) => a.id - b.id);
    const lastExecution = sorted.at(-1); // get the last (highest id)

    return normalizeExecutionStatus(lastExecution?.status);
};


export const isRecordExecutionCompleted = (record: VideoRecord, executionName?: string): boolean => {
    return getExecutionStatus(record, executionName) === "completed";
};

export const isRecordExecutionRunning = (record: VideoRecord, executionName?: string): boolean => {
    const status = getExecutionStatus(record, executionName);
    return status === "started";
};

export const isRecordExecutionInError = (record: VideoRecord, executionName?: string): boolean => {
    return getExecutionStatus(record, executionName) === "error";
};

export const nbExecutionsInError = (records: VideoRecord[], executionName?: string): number => {
    return records.reduce((count, record) => {
        return isRecordExecutionInError(record, executionName) ? count + 1 : count;
    }, 0);
};

export const nbExecutionsCompleted = (records: VideoRecord[], executionName?: string): number => {
    return records.reduce((count, record) => {
        return isRecordExecutionCompleted(record, executionName) ? count + 1 : count;
    }, 0);
};

export const nbExecutionsRunning = (records: VideoRecord[], executionName?: string): number => {
    return records.reduce((count, record) => {
        return isRecordExecutionCompleted(record, executionName) ? count + 1 : count;
    }, 0);
};



export const summarizeExecutions = (wf_types: string[], records: VideoRecord[]): RecordsExecutionsSummary[] => {
    const executionStats: { [key: string]: RecordsExecutionsSummary } = {};
    wf_types.forEach((type) => {
        executionStats[type] = {
            executionName: type,
            nbRecords: 0,
            nbRecordsCompleted: 0,
            nbRecordsInError: 0,
            nbRecordsRunning: 0
        };
    })
    records.forEach(record => {
        const latestExecutionsByType: { [key: string]: Execution } = {};

        // Sort executions by id, then take the latest per executionName
        const sortedExecutions = [...record.executions].sort((a, b) => a.id - b.id);
        sortedExecutions.forEach(exe => {
            latestExecutionsByType[exe.type] = exe; // override to keep latest
        });
        Object.entries(latestExecutionsByType).forEach(([type, execution]) => {
            /* if (!executionStats[type]) {
                executionStats[type] = {
                    executionName: type,
                    nbRecords: 0,
                    nbRecordsCompleted: 0,
                    nbRecordsInError: 0,
                    nbRecordsRunning: 0
                };
            } */
            if (wf_types.includes(type)) {//we ignore cases where old or irrelevant execution types
                const summary = executionStats[type];
                summary.nbRecords++;

                const status = normalizeExecutionStatus(execution.status);
                if (status === "completed") summary.nbRecordsCompleted++;
                else if (status === "error") summary.nbRecordsInError++;
                else if (status === "started") summary.nbRecordsRunning++
            }
            ;
        });
    });

    return Object.values(executionStats);
};
